import React, { useState } from 'react';
import { List, Copy, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const HeadingHierarchyTool = () => {
  const [html, setHtml] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [copied, setCopied] = useState(false);

  const analyzeHeadings = () => {
    if (!html.trim()) {
      setAnalysis(null);
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    
    const headingList = Array.from(headings).map((h, i) => ({
      level: parseInt(h.tagName[1]),
      text: h.textContent.trim(),
      index: i
    }));

    const issues = [];
    const suggestions = [];
    let score = 100;

    // Check for H1
    const h1Count = headingList.filter(h => h.level === 1).length;
    if (h1Count === 0) {
      issues.push({ type: 'error', text: 'Missing H1 tag - every page should have exactly one H1' });
      score -= 25;
    } else if (h1Count > 1) {
      issues.push({ type: 'warning', text: `Multiple H1 tags found (${h1Count}) - use only one H1 per page` });
      score -= 15;
    }

    // Check hierarchy order
    for (let i = 1; i < headingList.length; i++) {
      const prev = headingList[i - 1];
      const curr = headingList[i];
      
      if (curr.level > prev.level + 1) {
        issues.push({
          type: 'warning',
          text: `Skipped heading level: H${prev.level} → H${curr.level} (should not skip levels)`
        });
        score -= 10;
      }
    }

    // Check for empty headings
    const emptyHeadings = headingList.filter(h => !h.text);
    if (emptyHeadings.length > 0) {
      issues.push({ type: 'error', text: `${emptyHeadings.length} empty heading(s) found` });
      score -= 10 * emptyHeadings.length;
    }

    // Check heading length
    headingList.forEach(h => {
      if (h.text.length > 70) {
        issues.push({ type: 'info', text: `H${h.level} "${h.text.substring(0, 30)}..." is quite long` });
      }
    });

    // Generate suggestions
    if (h1Count === 0) {
      suggestions.push('Add an H1 heading that contains your main keyword');
    }
    if (headingList.filter(h => h.level === 2).length === 0 && headingList.length > 1) {
      suggestions.push('Consider using H2 headings to structure your content');
    }
    if (headingList.length < 3) {
      suggestions.push('Add more headings to improve content structure and readability');
    }

    // Build tree structure
    const tree = buildHeadingTree(headingList);

    setAnalysis({
      headings: headingList,
      tree,
      issues,
      suggestions,
      score: Math.max(0, score),
      counts: {
        h1: h1Count,
        h2: headingList.filter(h => h.level === 2).length,
        h3: headingList.filter(h => h.level === 3).length,
        h4: headingList.filter(h => h.level === 4).length,
        h5: headingList.filter(h => h.level === 5).length,
        h6: headingList.filter(h => h.level === 6).length,
      }
    });
  };

  const buildHeadingTree = (headings) => {
    return headings.map((h, i) => ({
      ...h,
      indent: (h.level - 1) * 20
    }));
  };

  const loadSample = () => {
    setHtml(`<h1>Ultimate Guide to SEO</h1>
<h2>What is SEO?</h2>
<p>SEO stands for Search Engine Optimization...</p>
<h2>On-Page SEO</h2>
<h3>Title Tags</h3>
<p>Title tags are important...</p>
<h3>Meta Descriptions</h3>
<p>Meta descriptions help...</p>
<h4>Writing Good Meta Descriptions</h4>
<h2>Off-Page SEO</h2>
<h3>Link Building</h3>
<h3>Social Signals</h3>
<h2>Technical SEO</h2>
<h3>Site Speed</h3>
<h3>Mobile Optimization</h3>`);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const copyOutline = () => {
    if (!analysis) return;
    const outline = analysis.headings.map(h => 
      '  '.repeat(h.level - 1) + `H${h.level}: ${h.text}`
    ).join('\n');
    navigator.clipboard.writeText(outline);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-300">HTML Content</label>
          <button
            onClick={loadSample}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            Load Sample
          </button>
        </div>
        <textarea
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          placeholder="Paste your HTML content here..."
          className="w-full h-48 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
        />
      </div>

      <button
        onClick={analyzeHeadings}
        disabled={!html.trim()}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        <List size={18} />
        Analyze Headings
      </button>

      {analysis && (
        <div className="space-y-6">
          {/* Score */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Heading Structure Score</span>
              <span className={`text-3xl font-bold ${getScoreColor(analysis.score)}`}>
                {analysis.score}/100
              </span>
            </div>
          </div>

          {/* Counts */}
          <div className="grid grid-cols-6 gap-2">
            {Object.entries(analysis.counts).map(([level, count]) => (
              <div key={level} className="p-3 bg-gray-800 rounded-lg text-center">
                <div className="text-lg font-bold text-white">{count}</div>
                <div className="text-xs text-gray-500">{level.toUpperCase()}</div>
              </div>
            ))}
          </div>

          {/* Issues */}
          {analysis.issues.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-300">Issues Found</h3>
              {analysis.issues.map((issue, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-2 p-3 rounded-lg ${
                    issue.type === 'error' ? 'bg-red-900/30 text-red-400' :
                    issue.type === 'warning' ? 'bg-yellow-900/30 text-yellow-400' :
                    'bg-blue-900/30 text-blue-400'
                  }`}
                >
                  {issue.type === 'error' ? <XCircle size={18} /> :
                   issue.type === 'warning' ? <AlertTriangle size={18} /> :
                   <CheckCircle size={18} />}
                  <span className="text-sm">{issue.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Heading Tree */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-300">Heading Outline</h3>
              <button
                onClick={copyOutline}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  copied ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {copied ? 'Copied!' : 'Copy Outline'}
              </button>
            </div>
            <div className="space-y-1">
              {analysis.tree.map((h, i) => (
                <div
                  key={i}
                  style={{ paddingLeft: `${h.indent}px` }}
                  className="flex items-center gap-2"
                >
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    h.level === 1 ? 'bg-purple-600' :
                    h.level === 2 ? 'bg-blue-600' :
                    h.level === 3 ? 'bg-green-600' :
                    'bg-gray-600'
                  }`}>
                    H{h.level}
                  </span>
                  <span className="text-sm text-gray-300 truncate">{h.text || '(empty)'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Suggestions */}
          {analysis.suggestions.length > 0 && (
            <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
              <h3 className="text-sm font-medium text-blue-300 mb-2">Suggestions</h3>
              <ul className="space-y-1 text-sm text-gray-400">
                {analysis.suggestions.map((s, i) => (
                  <li key={i}>• {s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeadingHierarchyTool;
